import { useEffect } from "react";

interface SEOHeadProps {
  title: string;
  description: string;
  canonicalUrl?: string;
  ogImage?: string;
  schema?: object;
  includeOrganizationSchema?: boolean;
}

// Organization schema for XperfoMax - added to all pages
const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "XperfoMax",
  description: "Systems-led B2B growth and performance marketing agency. We build interconnected growth systems that compound over time.",
  url: "https://xperfomax.com",
  logo: "https://xperfomax.com/og-image.png",
  sameAs: [],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "sales",
    url: "https://xperfomax.com/contact",
  },
  areaServed: "Worldwide",
  knowsAbout: [
    "Performance Marketing",
    "Growth Systems",
    "Paid Search",
    "Paid Social",
    "SEO",
    "Conversion Rate Optimization",
    "Analytics",
    "Marketing Automation",
  ],
};

const SEOHead = ({ 
  title, 
  description, 
  canonicalUrl, 
  ogImage, 
  schema,
  includeOrganizationSchema = true,
}: SEOHeadProps) => {
  useEffect(() => {
    // Update document title
    document.title = `${title} | XperfoMax`;

    // Update or create meta tags
    const updateMeta = (name: string, content: string, property?: boolean) => {
      const attr = property ? "property" : "name";
      let meta = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    updateMeta("description", description);
    updateMeta("og:title", title, true);
    updateMeta("og:description", description, true);
    updateMeta("og:type", "website", true);
    
    if (ogImage) {
      updateMeta("og:image", ogImage, true);
    }

    // Update or create canonical link
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
      }
      link.href = canonicalUrl;
    }

    // Add Organization schema (once per page load)
    if (includeOrganizationSchema) {
      const existingOrgSchema = document.querySelector('script[data-schema="organization"]');
      if (!existingOrgSchema) {
        const orgScript = document.createElement("script");
        orgScript.type = "application/ld+json";
        orgScript.setAttribute("data-schema", "organization");
        orgScript.textContent = JSON.stringify(organizationSchema);
        document.head.appendChild(orgScript);
      }
    }

    // Add page-specific schema markup
    if (schema) {
      const existingSchema = document.querySelector('script[data-schema="page"]');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-schema", "page");
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup page schema on unmount
      const schemaScript = document.querySelector('script[data-schema="page"]');
      if (schemaScript) {
        schemaScript.remove();
      }
    };
  }, [title, description, canonicalUrl, ogImage, schema, includeOrganizationSchema]);

  return null;
};

export default SEOHead;
